import React from 'react';
import { useQuery } from '@apollo/client';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { loader } from 'graphql.macro';
import { useTranslation } from 'react-i18next';
import { GetCaseFiles, GetCaseFilesVariables } from '../../../GraphQL';
import dateToDateTimeString from '@ssg/common/Helpers/dateToDateTimeString';
import TextButton from '@ssg/common/Components/TextButton';
import Table from '@ssg/common/Components/Table';

interface Props {
	id: string;
}

const GET_CASE_FILES = loader('src/GraphQL/Files/GetCaseFiles.gql');

const CaseMailsTab: React.FC<Props> = ({ id }) => {
	const { t } = useTranslation();

	const { data } = useQuery<GetCaseFiles, GetCaseFilesVariables>(GET_CASE_FILES, { variables: { caseErpNo: id } });

	const emails = React.useMemo(
		() =>
	 	(data?.caseFiles ?? []).filter(file => file.name.includes('.eml')).sort((a, b) => {
			const dateA = a.metadata.find(m => m.key === 'Created')?.value;
	 		const dateB = b.metadata.find(m => m.key === 'Created')?.value;
	 		return dateA && dateB ? new Date(dateB).getTime() - new Date(dateA).getTime() : 0;
	 	}),
	 [data],
	 );

	if (emails.length === 0) return <div className="text-blue text-center font-medium">{t('file.noMails')}</div>;

	return (
		<Table 
			data={emails}
			keySelector={e => e.name}
			noDataFoundText="Ingen mails fundet"
			columns={[
				{
					label: 'common.name',
					selectFn: e => (
						<a href={e.url} target="_blank" rel="noreferrer">
							<TextButton link text={e.name} />
						</a>
					),
				},
				{
					label: 'mail.sentTo',
					selectFn: e => e.metadata.find(m => m.key === 'Mailtil')?.value ?? (t('common.notSpecified') as string),
				},
				{
					label: 'mail.CC',
					selectFn: e => e.metadata.find(m => m.key === 'MailCC')?.value ?? (t('common.notSpecified') as string),
				},
				{
					label: 'mail.dateSent',
					selectFn: e => {
						const createdAt = e.metadata.find(m => m.key === 'Created')?.value;
						return createdAt ? dateToDateTimeString(createdAt) : '';
					},
				},
				{
					label: 'common.download',
					selectFn: e => (
						<a href={e.url} target="_blank" rel="noreferrer">
							<TextButton icon={faEnvelope} text="case.mailEml" className="pr-4" />
						</a>
					),
				},
			]}
		/>
	);
};

export default CaseMailsTab;